import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import './UserManagementPage.css';
import { API_BASE_URL } from '../services/api';
import { validatePassword } from '../utils/passwordValidator';

const ALL_TABS = [
  { key: 'gsec_report', label: 'T Bond Report', path: '/gsec-report' },
  { key: 'tbill_report', label: 'T-Bill Report', path: '/tbill-report' },
  { key: 'gsec_summary_report', label: 'T Bond Summary Report', path: '/gsec-summary-report' },
  { key: 'money-market-report', label: 'Money Market Report', path: '/reports/money-market' },
  { key: 'mark-to-market-report', label: 'Mark to Market Report', path: '/reports/mark-to-market' },
  { key: 'repo-report', label: 'Repo / Reverse Repo Report', path: '/reports/repo' },
  { key: 'portfolio-report', label: 'Portfolio Report', path: '/portfolio-report' },
  { key: 'counterparty-report', label: 'Counterparty Transaction Report', path: '/counterparty-report' },
  { key: 'counterparty-master-report', label: 'Counterparty Detail Report', path: '/reports/counterparty-master' },
  { key: 'buyback-report', label: 'Buyback Report', path: '/reports/buyback' },
  { key: 'coupon-maturity-blotter', label: 'Coupon Maturity Blotter', path: '/coupon-maturity-blotter' },
  { key: 'transactions', label: 'Transactions', path: '/transactions' },
  { key: 'payment_master', label: 'Payment Master', path: '/payment-master' },
  { key: 'isin_master', label: 'ISIN Master', path: '/isin-master' },
  { key: 'counterparty_master', label: 'Counterparty Master', path: '/counterparty-master' },
  // limit_setup is intentionally NOT listed here: Counterparty Limit Setup is
  // exclusive to Middle Office tiers, assigned via the dedicated "Middle
  // Office Access" admin panel, not the general per-user tab grant.
  { key: 'fixed_income_gsec', label: 'Fixed Income Gsec', path: '/fixed-income-gsec' },
  { key: 'fixed_income_tbill', label: 'Fixed Income T-Bill', path: '/fixed-income-tbill' },
  { key: 'fixed_income_others', label: 'Fixed Income Others', path: '/fixed-income-others' },
  { key: 'money_market', label: 'Money Market', path: '/money-market' },
  { key: 'fixed_deposit', label: 'Fixed Deposit', path: '/fixed-deposit' },
  { key: 'payments_rtgs', label: 'Payments - RTGS', path: '/payments-rtgs' },
  { key: 'payments_other', label: 'Payments - Other', path: '/payments-other' },
  { key: 'repo', label: 'Repo', path: '/repo' },
  { key: 'reverse_repo', label: 'Mark to Market', path: '/mark-to-market' },
  { key: 'portfolio_master', label: 'Portfolio Master', path: '/portfolio-master' },
  { key: 'broker_master', label: 'Broker Master', path: '/broker-master' },
  { key: 'strategy_master', label: 'Strategy Master', path: '/strategy-master' },
  { key: 'buyback', label: 'Fixed Income Buyback', path: '/fixed-income-buy-back' },
  { key: 'maturity', label: 'Maturity Handling', path: '/maturity' },
  { key: 'maturity_authorization', label: 'Maturity Authorization Blotter', path: '/maturity-front-office-blotter' },
  { key: 'daily-deal-blotter', label: 'Daily Deal Blotter', path: '/daily-deal-blotter' },
  { key: 'daily-cashflow-blotter', label: 'Daily Cashflow Blotter', path: '/daily-cashflow-blotter' },
  { key: 'daily-transaction-blotter', label: 'Daily Transaction Blotter', path: '/daily-transaction-blotter' },
  { key: 'admin_users', label: 'Admin - Users', path: '/admin/users' },
  { key: 'admin_accounting', label: 'Admin - Accounting', path: '/admin/accounting' },
  { key: 'admin_settlement_preview', label: 'Settlement Preview', path: '/admin/settlement-preview' },
  { key: 'bank_charges', label: 'Vouchers', path: '/bank-charges' },
  { key: 'interest_income_report', label: 'Interest Income Report', path: '/reports/interest-income' },
  { key: 'settlement_account_master', label: 'Settlement Account Master', path: '/admin/settlement-account-master' },
  { key: 'holiday_calendar', label: 'Holiday Calendar', path: '/holiday-calendar' },
  { key: 'fund_centre_master', label: 'Fund Centre Master', path: '/fund-centre-master' },
  { key: 'issuer_master', label: 'Issuer Master', path: '/issuer-master' },
  { key: 'investment_approver_master', label: 'Investment Approver Master', path: '/investment-approver-master' }
];

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    allowed_tabs: []
  });


  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editTabs, setEditTabs] = useState([]);
  
  // Password reset modal state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [resetSuccess, setResetSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('Fetching users from API...');
      
      const response = await axios.get(`${API_BASE_URL}/users`);
      console.log('Users API response:', response);
      
      // Handle the response data
      if (response.data) {
        setUsers(Array.isArray(response.data) ? response.data : []);
        setError('');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Failed to fetch users:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      });
      
      // Show error message
      setError('Failed to fetch users: ' + err.message);
    }
  };

  const handleTabChange = (tabKey) => {
    setFormData(prev => {
      const exists = prev.allowed_tabs.includes(tabKey);
      return {
        ...prev,
        allowed_tabs: exists
          ? prev.allowed_tabs.filter(k => k !== tabKey)
          : [...prev.allowed_tabs, tabKey]
      };
    });
  };

  const handleEditTabChange = (tabKey) => {
    setEditTabs(prev => {
      const exists = prev.includes(tabKey);
      return exists ? prev.filter(k => k !== tabKey) : [...prev, tabKey];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Sending registration request:', formData);
      const response = await axios.post(`${API_BASE_URL}/auth/register`, formData);
      console.log('Registration response:', response.data);
      if (!response.data.success && !response.data.id) {
        throw new Error(response.data.message || response.data.error || 'Failed to create user');
      }
      setShowModal(false);
      setFormData({ username: '', password: '', role: 'user', allowed_tabs: [] });
      fetchUsers();
    } catch (err) {
      console.error('Registration error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url
      });
      setError(err.response?.data?.error || err.message || 'Failed to create user');
    }
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditTabs(user.allowed_tabs || []);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE_URL}/user/${editUser.id}/tabs`, { allowed_tabs: editTabs });
      setShowEditModal(false);
      setEditUser(null);
      setEditTabs([]);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update user tabs');
    }
  };

  // Open password reset modal
  const openPasswordResetModal = (user) => {
    setResetUser(user);
    setResetPassword('');
    setConfirmResetPassword('');
    setPasswordValidation(null);
    setError('');
    setResetSuccess('');
    setShowPasswordResetModal(true);
  };

  // Handle password reset form submission
  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    if (!resetPassword || !confirmResetPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (resetPassword !== confirmResetPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validation = validatePassword(resetPassword);
    if (!validation.isValid) {
      setError('Password does not meet security requirements');
      return;
    }

    setPasswordResetLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/user/${resetUser.id}/admin-reset-password`,
        { newPassword: resetPassword },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setResetSuccess(`Password reset successfully for user: ${resetUser.username}`);
        setResetPassword('');
        setConfirmResetPassword('');
        setPasswordValidation(null);
        
        // Close modal after success
        setTimeout(() => {
          setShowPasswordResetModal(false);
          setResetUser(null);
          setResetSuccess('');
        }, 2000);
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to reset password');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  // Handle password input change with validation
  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'resetPassword') {
      setResetPassword(value);
      setPasswordValidation(validatePassword(value));
    } else if (name === 'confirmResetPassword') {
      setConfirmResetPassword(value);
    }

    // Clear messages when user types
    if (error || resetSuccess) {
      setError('');
      setResetSuccess('');
    }
  };

  return (
    <div className="user-management-container ui-page">
      <div className="user-management-header">
        <button 
          className="add-user-btn" 
          onClick={() => setShowModal(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
          Add New User
        </button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <div className="search-add-container">
        <input
          type="text"
          className="form-control"
          placeholder="Search users by username or role..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ maxWidth: 300 }}
        />
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Username</th>
            <th>Role</th>
            <th>Created At</th>
            <th>Allowed Tabs</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users
            .filter(user =>
              user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.role.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td className={user.role === 'authorizer' ? 'text-success fw-bold' : ''}>
                  {user.role}
                </td>
                <td>{new Date(user.created_at).toLocaleString()}</td>
                <td>{user.allowed_tabs && user.allowed_tabs.length > 0 ? user.allowed_tabs.map(tab => ALL_TABS.find(t => t.key === tab)?.label || tab).join(', ') : 'None'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button className="action-button" onClick={() => openEditModal(user)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '5px' }}>
                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                      </svg>
                      Edit
                    </button>
                    <button 
                      className="action-button" 
                      onClick={() => openPasswordResetModal(user)}
                      style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                      title="Reset Password"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '5px' }}>
                        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zM6 7v4a4 4 0 0 0 8 0V7a1 1 0 0 0-1-1V3a3 3 0 0 0-6 0v3a1 1 0 0 0-1 1z"/>
                      </svg>
                      Reset
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {/* Create User Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        dialogClassName="user-modal-centered"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit} className="user-creation-form">
            <h3 className="form-title">Enter User Details</h3>
            
            <div className="form-field">
              <label htmlFor="username">Username</label>
              <input 
                id="username"
                type="text" 
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Enter username"
                required
              />
            </div>
            
            <div className="form-field">
              <label htmlFor="password">Password</label>
              <input 
                id="password"
                type="password" 
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Enter password"
                required
              />
            </div>
            
            <div className="form-field">
              <label htmlFor="role">Select Role</label>
              <select
                id="role"
                value={formData.role}
                onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="authorizer">Authorizer</option>
                <option value="limits_allocating_user">Limits Allocating User</option>
                <option value="limits_allocating_authorizer">Limits Allocating Authorizer</option>
              </select>
            </div>
            
            <div className="form-field">
              <label>Allowed Tabs</label>
              <div className="checkbox-group">
                {ALL_TABS.map(tab => (
                  <div key={tab.key} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`tab-${tab.key}`}
                      checked={formData.allowed_tabs.includes(tab.key)}
                      onChange={() => handleTabChange(tab.key)}
                      data-testid={`tab-checkbox-${tab.key}`}
                    />
                    <label htmlFor={`tab-${tab.key}`}>{tab.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <button className="submit-button" type="submit">
              Create User
            </button>
          </Form>
        </Modal.Body>
      </Modal>
      {/* Edit User Tabs Modal */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
        dialogClassName="user-modal-centered"
      >
        <Modal.Header closeButton>
          <Modal.Title>Modify Allowed Tabs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit} className="user-creation-form">
            <h3 className="form-title">Edit User Permissions</h3>
            
            <div className="form-field">
              <label>Allowed Tabs for User</label>
              <div className="checkbox-group">
                {ALL_TABS.map(tab => (
                  <div key={tab.key} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`edit-tab-${tab.key}`}
                      checked={editTabs.includes(tab.key)}
                      onChange={() => handleEditTabChange(tab.key)}
                    />
                    <label htmlFor={`edit-tab-${tab.key}`}>{tab.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <button className="submit-button" type="submit">Save Changes</button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Password Reset Modal */}
      <Modal 
        show={showPasswordResetModal} 
        onHide={() => setShowPasswordResetModal(false)}
        centered
        size="md"
        dialogClassName="user-modal-centered"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset User Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handlePasswordResetSubmit} className="user-creation-form">
            <h5 className="form-title">
              Reset password for: <strong>{resetUser?.username}</strong>
            </h5>

            {resetSuccess && (
              <Alert variant="success" style={{ fontSize: '0.9rem' }}>
                {resetSuccess}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" style={{ fontSize: '0.9rem' }}>
                {error}
              </Alert>
            )}
            
            <div className="form-field">
              <label htmlFor="resetPassword">New Password</label>
              <div style={{ position: 'relative' }}>
                <input 
                  id="resetPassword"
                  type={showResetPassword ? 'text' : 'password'}
                  name="resetPassword"
                  value={resetPassword}
                  onChange={handleResetPasswordChange}
                  placeholder="Enter new password"
                  disabled={passwordResetLoading}
                  style={{ paddingRight: '2.5rem' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '0.9rem'
                  }}
                >
                  {showResetPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {passwordValidation && resetPassword && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{
                    height: '4px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${passwordValidation.score}%`,
                        backgroundColor: passwordValidation.score < 30 ? '#dc3545' : 
                                       passwordValidation.score < 50 ? '#fd7e14' :
                                       passwordValidation.score < 70 ? '#ffc107' :
                                       passwordValidation.score < 85 ? '#20c997' : '#28a745',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '0.25rem',
                    fontSize: '0.8rem'
                  }}>
                    <span style={{ 
                      color: passwordValidation.score < 30 ? '#dc3545' : 
                             passwordValidation.score < 50 ? '#fd7e14' :
                             passwordValidation.score < 70 ? '#ffc107' :
                             passwordValidation.score < 85 ? '#20c997' : '#28a745',
                      fontWeight: '500' 
                    }}>
                      {passwordValidation.strength}
                    </span>
                    <span style={{ color: '#666' }}>
                      {passwordValidation.score}/100
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="form-field">
              <label htmlFor="confirmResetPassword">Confirm New Password</label>
              <input 
                id="confirmResetPassword"
                type={showResetPassword ? 'text' : 'password'}
                name="confirmResetPassword"
                value={confirmResetPassword}
                onChange={handleResetPasswordChange}
                placeholder="Confirm new password"
                disabled={passwordResetLoading}
                required
              />
              {confirmResetPassword && resetPassword !== confirmResetPassword && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Passwords do not match
                </div>
              )}
            </div>

            {/* Password Requirements */}
            {passwordValidation && !passwordValidation.isValid && resetPassword && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '5px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.85rem'
              }}>
                <div style={{ fontWeight: '500', marginBottom: '0.5rem', color: '#856404' }}>
                  Password Requirements:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#856404' }}>
                  <li>At least 8 characters long</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                  <li>One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)</li>
                  <li>No spaces allowed</li>
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                type="button"
                className="submit-button"
                onClick={() => setShowPasswordResetModal(false)}
                style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}
                disabled={passwordResetLoading}
              >
                Cancel
              </button>
              <button 
                className="submit-button" 
                type="submit"
                disabled={passwordResetLoading || !passwordValidation?.isValid || resetPassword !== confirmResetPassword}
                style={{ 
                  backgroundColor: passwordResetLoading || !passwordValidation?.isValid ? '#ccc' : '#dc3545',
                  borderColor: passwordResetLoading || !passwordValidation?.isValid ? '#ccc' : '#dc3545'
                }}
              >
                {passwordResetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </Form>

          {/* Warning Notice */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '5px',
            fontSize: '0.85rem',
            color: '#721c24'
          }}>
            <strong>⚠️ Warning:</strong> This will immediately reset the user's password. 
            The user will need to use the new password to log in. If the user has an email 
            address configured, they will receive a notification about the password change.
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
